import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectDarkMode } from "../../../../app/store/slices/uiSlice";
import { XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

const LIMIT = 100;

export default function InfiniteMultiSelect({
  name,
  value = [],
  onChange,
  fetchFn,
  getOptions,
  getTotal,
  extraParams = {},
  disabled = false,
  placeholder = "Select options",
}) {
  const darkMode = useSelector(selectDarkMode);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const containerRef = useRef(null);
  const sentinelRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const observerRef = useRef(null);

  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];

  const fetchPage = useCallback(
    async (pageNum, searchTerm, append = false) => {
      if (loading) return;
      setLoading(true);
      try {
        const res = await fetchFn({ page: pageNum, limit: LIMIT, search: searchTerm, sortBy: "name", order: "asc", ...extraParams });
        const fetched = getOptions(res);
        setOptions((prev) => (append ? [...prev, ...fetched] : fetched));
        const total = getTotal ? getTotal(res) : null;
        setHasMore(total !== null ? pageNum * LIMIT < total : fetched.length === LIMIT);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    },
    [fetchFn, getOptions, extraParams]
  );

  useEffect(() => {
    if (selectedValues.length > 0) fetchPage(1, "", false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen) {
      setSearch("");
      setPage(1);
      setHasMore(true);
      fetchPage(1, "", false);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return;
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPage(1);
      setHasMore(true);
      fetchPage(1, search, false);
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchPage(nextPage, search, true);
        }
      },
      { threshold: 1 }
    );
    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [isOpen, hasMore, loading, page, search, fetchPage]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggleOption = (opt) => {
    const next = selectedValues.includes(opt.value)
      ? selectedValues.filter((v) => v !== opt.value)
      : [...selectedValues, opt.value];
    onChange({ target: { name, value: next } });
  };

  const removeOption = (val, e) => {
    e.stopPropagation();
    const next = selectedValues.filter((v) => v !== val);
    onChange({ target: { name, value: next } });
  };

  const selectedLabels = selectedValues.map((v) => ({
    value: v,
    label: options.find((o) => o.value === v)?.label || v,
  }));

  const triggerBorder = darkMode ? "border-gray-600" : "border-gray-300";
  const triggerBg = darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900";
  const dropdownBg = darkMode ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200";
  const itemHover = darkMode ? "hover:bg-gray-700" : "hover:bg-blue-50";

  return (
    <div className="w-full relative" ref={containerRef}>
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((o) => !o)}
        className={`w-full min-h-[42px] border rounded-lg px-3 py-1.5 text-left flex items-center gap-2 flex-wrap focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${triggerBorder} ${triggerBg} ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      >
        {selectedLabels.length === 0 ? (
          <span className="text-gray-400 text-sm py-0.5">{placeholder}</span>
        ) : (
          selectedLabels.map((item) => (
            <span
              key={item.value}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${darkMode ? "bg-blue-900 text-blue-200" : "bg-blue-100 text-blue-700"}`}
            >
              {item.label}
              <XMarkIcon
                className="w-3 h-3 cursor-pointer hover:opacity-70"
                onClick={(e) => removeOption(item.value, e)}
              />
            </span>
          ))
        )}
        <ChevronDownIcon className={`w-4 h-4 text-gray-400 ml-auto flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className={`absolute z-50 mt-1 w-full border rounded-lg shadow-lg overflow-hidden ${dropdownBg}`}>
          <div className={`p-2 border-b ${darkMode ? "border-gray-600" : "border-gray-100"}`}>
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className={`w-full px-3 py-1.5 text-sm rounded border focus:outline-none focus:ring-1 focus:ring-blue-500 ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
            />
          </div>

          <ul className="overflow-y-auto max-h-52">
            {options.length === 0 && !loading && (
              <li className={`px-4 py-3 text-sm text-center ${darkMode ? "text-gray-400" : "text-gray-500"}`}>No results found</li>
            )}
            {options.map((opt) => {
              const isSelected = selectedValues.includes(opt.value);
              return (
                <li
                  key={opt.value}
                  onClick={() => toggleOption(opt)}
                  className={`px-4 py-2.5 text-sm cursor-pointer flex items-center gap-2 transition-colors ${itemHover} ${isSelected ? (darkMode ? "bg-blue-900 text-blue-200" : "bg-blue-50 text-blue-700") : darkMode ? "text-gray-200" : "text-gray-800"}`}
                >
                  <span className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center ${isSelected ? "bg-blue-600 border-blue-600" : darkMode ? "border-gray-500" : "border-gray-300"}`}>
                    {isSelected && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  {opt.label}
                </li>
              );
            })}
            <li ref={sentinelRef} className="h-1" />
            {loading && (
              <li className={`px-4 py-2 text-xs text-center ${darkMode ? "text-gray-400" : "text-gray-400"}`}>Loading...</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
